// components/branches/config/AppointmentConfigManagement.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  CalendarIcon,
  BellIcon,
  CogIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  BanIcon,
  AdjustmentsIcon,
} from "@heroicons/react/outline";
import {
  getAppointmentConfigThunk,
  updateAppointmentConfigThunk,
  setConfigEditMode,
  updateDayBuffersThunk,
  updateSchedulingRulesThunk,
} from "../../../store/thunks/branchAppointmentConfigThunks";
import GeneralSettings from "./appointment/GeneralSettings";
import AutoApprovalSettings from "./appointment/AutoApprovalSettings";
import BookingWindowSettings from "./appointment/BookingWindowSettings";
import AppointmentTypesSettings from "./appointment/AppointmentTypesSettings";
import SchedulingRulesSettings from "./appointment/SchedulingRulesSettings";
import NotificationSettings from "./appointment/NotificationSettings";
import CancellationPolicySettings from "./appointment/CancellationPolicySettings";
import CapacityManagementSettings from "./appointment/CapacityManagementSettings";
import DayBuffersSettings from "./appointment/DayBuffersSettings";
import BlockedPeriodsSettings from "./appointment/BlockedPeriodsSettings";
import { setHasUnsavedChanges } from "store/slices/branchAppointmentConfigSlice";

const AppointmentConfigManagement = ({ branch }) => {
  const dispatch = useDispatch();
  const {
    appointmentConfig,
    loadingConfig,
    loading,
    configError,
    isEditMode,
    hasUnsavedChanges,
  } = useSelector((state) => state.branchAppointmentConfig);

  const [activeSection, setActiveSection] = useState("general");
  const [localConfig, setLocalConfig] = useState(null);

  useEffect(() => {
    if (branch?.code) {
      dispatch(getAppointmentConfigThunk(branch.code));
    }
  }, [dispatch, branch?.code]);

  useEffect(() => {
    if (appointmentConfig) {
      setLocalConfig(appointmentConfig);
    }
  }, [appointmentConfig]);

  const handleEdit = () => {
    dispatch(setConfigEditMode(true));
  };

  const handleCancel = () => {
    setLocalConfig(appointmentConfig);
    dispatch(setConfigEditMode(false));
    dispatch(setHasUnsavedChanges(false));
  };

  const handleSave = async () => {
    try {
      // For day buffers and blocked periods, use their specific endpoints
      if (activeSection === "dayBuffers") {
        await dispatch(
          updateDayBuffersThunk({
            branchCode: branch.code,
            dayBuffers: localConfig.schedulingRules?.dayBuffers,
          })
        ).unwrap();
      } else if (activeSection === "schedulingRules") {
        await dispatch(
          updateSchedulingRulesThunk({
            branchCode: branch.code,
            schedulingRules: localConfig.schedulingRules,
          })
        ).unwrap();
      } else {
        // For other sections, use the general update
        await dispatch(
          updateAppointmentConfigThunk({
            branchCode: branch.code,
            configData: localConfig,
          })
        ).unwrap();
      }
      alert("Appointment configuration updated successfully!");
    } catch (error) {
      alert(`Failed to update configuration: ${error}`);
    }
  };

  const handleConfigChange = (section, value) => {
    setLocalConfig({
      ...localConfig,
      [section]: value,
    });
    dispatch(setHasUnsavedChanges(true));
  };

  const handleDayBuffersChange = (updatedDayBuffers) => {
    setLocalConfig({
      ...localConfig,
      schedulingRules: {
        ...localConfig.schedulingRules,
        dayBuffers: updatedDayBuffers,
      },
    });
    dispatch(setHasUnsavedChanges(true));
  };

  const handleSchedulingRulesChange = (updatedRules) => {
    setLocalConfig({
      ...localConfig,
      schedulingRules: updatedRules,
    });
    dispatch(setHasUnsavedChanges(true));
  };

  // Special handler for GeneralSettings which updates root-level config
  const handleGeneralSettingsChange = (updatedConfig) => {
    setLocalConfig(updatedConfig);
    dispatch(setHasUnsavedChanges(true));
  };

  const sections = [
    { id: "general", label: "General", icon: CogIcon },
    { id: "autoApproval", label: "Auto-Approval", icon: CheckCircleIcon },
    { id: "bookingWindow", label: "Booking Window", icon: CalendarIcon },
    { id: "appointmentTypes", label: "Appointment Types", icon: UserGroupIcon },
    { id: "dayBuffers", label: "Day Buffers", icon: AdjustmentsIcon },
    { id: "blockedPeriods", label: "Blocked Periods", icon: BanIcon },
    { id: "schedulingRules", label: "Scheduling", icon: ClockIcon },
    { id: "notifications", label: "Notifications", icon: BellIcon },
    { id: "cancellationPolicy", label: "Cancellation", icon: XCircleIcon },
    { id: "capacityManagement", label: "Capacity", icon: ChartBarIcon },
  ];

  if (loadingConfig) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (configError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-600">
          Error loading configuration: {configError}
        </p>
      </div>
    );
  }

  if (!localConfig) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-yellow-800 mb-2">
              No Appointment Configuration
            </h3>
            <p className="text-yellow-700">
              This branch doesn't have appointment configuration yet. Click
              below to set up appointments.
            </p>
          </div>
          <button
            onClick={() => {
              // Initialize with default config
              setLocalConfig({
                enabled: false,
                autoApproval: {
                  enabled: false,
                  conditions: {
                    withinDays: 7,
                    maxPerDay: 20,
                    highValueThreshold: 100000,
                    requiresAvailabilityCheck: true,
                    requiresAdminAssignment: true,
                    excludeFirstTimeCustomers: false,
                    autoApproveTypes: [],
                  },
                },
                bookingWindow: {
                  advanceBookingDays: 14,
                  maxAdvanceDays: 30,
                  minAdvanceHours: 2,
                  sameDayBooking: {
                    enabled: true,
                    cutoffTime: "15:00",
                  },
                  afterHoursBooking: {
                    enabled: false,
                    requiresApproval: true,
                  },
                },
                appointmentTypes: [],
                schedulingRules: {
                  defaultSlotDuration: 30,
                  bufferBetweenAppointments: 10,
                  maxConcurrentAppointments: 3,
                  maxAppointmentsPerSlot: 1,
                  walkIns: {
                    allowed: true,
                    reservedSlotsPerDay: 2,
                    priority: "APPOINTMENT",
                  },
                  dayBuffers: {
                    startBuffer: {
                      enabled: true,
                      minutes: 30,
                      reason: "Store setup and preparation",
                    },
                    endBuffer: {
                      enabled: true,
                      minutes: 45,
                      reason: "Closing procedures",
                    },
                    applyToDays: [
                      "monday",
                      "tuesday",
                      "wednesday",
                      "thursday",
                      "friday",
                    ],
                  },
                  blockedPeriods: [],
                },
                notifications: {
                  enabled: true,
                },
                cancellationPolicy: {
                  customerCanCancel: true,
                  minHoursBeforeAppointment: 2,
                  noShowPolicy: {
                    enabled: true,
                    maxNoShows: 3,
                    restrictionDays: 30,
                    penaltyType: "PHONE_VERIFY_ONLY",
                  },
                  rescheduling: {
                    allowed: true,
                    maxReschedules: 2,
                    minHoursBefore: 4,
                  },
                },
                capacityManagement: {
                  maxAppointmentsPerDay: 20,
                  maxAppointmentsPerHour: 4,
                },
                analytics: {
                  trackMetrics: true,
                },
              });
              dispatch(setConfigEditMode(true));
            }}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Initialize Configuration
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold">Appointment Configuration</h2>
            <p className="text-gray-600 mt-1">
              Manage appointment settings for {branch.name}
            </p>
          </div>
          <div className="flex gap-2">
            {!isEditMode ? (
              <button
                onClick={handleEdit}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Edit Configuration
              </button>
            ) : (
              <>
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={!hasUnsavedChanges || loading}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? "Saving..." : "Save Changes"}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Status Badge */}
        <div className="mt-4 flex items-center gap-2">
          <span
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              localConfig.enabled
                ? "bg-green-100 text-green-800"
                : "bg-red-100 text-red-800"
            }`}
          >
            {localConfig.enabled
              ? "Appointments Enabled"
              : "Appointments Disabled"}
          </span>
          {hasUnsavedChanges && (
            <span className="px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
              Unsaved Changes
            </span>
          )}
        </div>
      </div>

      {/* Navigation */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="border-b">
          <nav className="flex overflow-x-auto">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`px-4 py-3 font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${
                    activeSection === section.id
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-gray-600 hover:text-gray-900"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {section.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="p-6">
          {activeSection === "general" && (
            <GeneralSettings
              config={localConfig}
              onChange={handleGeneralSettingsChange}
              isEditMode={isEditMode}
            />
          )}

          {activeSection === "autoApproval" && (
            <AutoApprovalSettings
              config={localConfig.autoApproval || {}}
              onChange={(value) => handleConfigChange("autoApproval", value)}
              isEditMode={isEditMode}
            />
          )}

          {activeSection === "bookingWindow" && (
            <BookingWindowSettings
              config={localConfig.bookingWindow || {}}
              onChange={(value) => handleConfigChange("bookingWindow", value)}
              isEditMode={isEditMode}
            />
          )}

          {activeSection === "appointmentTypes" && (
            <AppointmentTypesSettings
              types={localConfig.appointmentTypes || []}
              onChange={(value) =>
                handleConfigChange("appointmentTypes", value)
              }
              isEditMode={isEditMode}
            />
          )}

          {activeSection === "dayBuffers" && (
            <DayBuffersSettings
              config={localConfig.schedulingRules?.dayBuffers || {}}
              onChange={handleDayBuffersChange}
              isEditMode={isEditMode}
            />
          )}

          {activeSection === "blockedPeriods" && (
            <BlockedPeriodsSettings
              branchCode={branch.code}
              isEditMode={isEditMode}
            />
          )}

          {activeSection === "schedulingRules" && (
            <SchedulingRulesSettings
              config={localConfig.schedulingRules || {}}
              onChange={handleSchedulingRulesChange}
              isEditMode={isEditMode}
            />
          )}

          {activeSection === "notifications" && (
            <NotificationSettings
              config={localConfig.notifications || {}}
              onChange={(value) => handleConfigChange("notifications", value)}
              isEditMode={isEditMode}
            />
          )}

          {activeSection === "cancellationPolicy" && (
            <CancellationPolicySettings
              config={localConfig.cancellationPolicy || {}}
              onChange={(value) =>
                handleConfigChange("cancellationPolicy", value)
              }
              isEditMode={isEditMode}
            />
          )}

          {activeSection === "capacityManagement" && (
            <CapacityManagementSettings
              config={localConfig.capacityManagement || {}}
              onChange={(value) =>
                handleConfigChange("capacityManagement", value)
              }
              isEditMode={isEditMode}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AppointmentConfigManagement;
